/**
 * @typedef {Object} DeliveryLocationData
 * @property {string} address
 * @property {number} lat
 * @property {number} lng
 * @property {string} mapsUrl
 * @property {string} placeId
 * @property {string} source
 * @property {string} selectedAt
 */

/**
 * Shared, DOM-lifecycle-independent access to the shopper's saved delivery
 * location. Kept as a plain module (rather than reading state off the
 * `<delivery-location-modal>` singleton directly) so any Add to Cart entry
 * point can check validity even if that element hasn't upgraded/connected
 * yet — reading storage directly here has no such race.
 */

const STORAGE_KEY = 'delivery-location:global';

function getModal() {
  return document.getElementById('delivery-location-modal');
}

function getStorage() {
  const storageType = getModal()?.dataset.storageType === 'session' ? 'sessionStorage' : 'localStorage';
  return window[storageType];
}

/**
 * @returns {DeliveryLocationData | null}
 */
export function getSavedLocation() {
  try {
    const raw = getStorage().getItem(STORAGE_KEY);
    if (!raw) return null;

    const data = JSON.parse(raw);
    if (!data || typeof data.address !== 'string' || !data.address) return null;

    return data;
  } catch {
    return null;
  }
}

/**
 * @returns {boolean}
 */
export function hasValidLocation() {
  return Boolean(getSavedLocation()?.address);
}

/**
 * Resolves `true` immediately if a valid location is already saved.
 * Otherwise opens the global delivery-location modal and resolves once the
 * shopper proceeds (`true`) or cancels/dismisses it (`false`). Safe to call
 * from any Add to Cart entry point before its existing cart/add.js request.
 * @returns {Promise<boolean>}
 */
export async function ensureDeliveryLocation() {
  if (hasValidLocation()) return true;

  const modal = getModal();
  if (!modal || typeof modal.requestSelection !== 'function') {
    // Feature disabled or modal not present — never silently block Add to Cart.
    return true;
  }

  return modal.requestSelection();
}

// Bridge for non-module script contexts (e.g. Shopify's {% javascript %} tag
// output, which shouldn't be assumed to resolve this theme's importmap bare
// specifiers) that still need to gate their own Add to Cart calls. Assigned
// as an explicit `window` property (not merged onto the theme's `Theme`
// global, which is declared with `const` in a classic script and therefore
// isn't reachable via `window.Theme` from other script contexts) so any
// script on the page can reliably reach it the same way.
window.ThemeDeliveryLocation = {
  ensure: ensureDeliveryLocation,
  hasValidLocation,
  getSavedLocation,
};
